import type { VariantProps } from 'class-variance-authority'
import type { LucideIcon } from 'lucide-react'

import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { riskLevelLabel } from '@/const/label'

import { cn } from '@/lib/ui/style-helpers'

import { Badge } from '@/components/lemmala-design-system/badge/badge'
import { RISK_LEVEL_MAP } from '@/components/shared/risk-badge/consts'
import type { RiskBadgeVariant } from '@/components/shared/risk-badge/types'
import { estimatedBorderVariants, iconVariants } from '@/components/shared/risk-badge/variants'

type SupportedRiskLevel = NonNullable<VariantProps<typeof estimatedBorderVariants>['riskLevel']>

export type RiskBadgeProps = {
  riskLevel: RiskLevel
  variant?: RiskBadgeVariant
  icon?: LucideIcon
} & VariantProps<typeof iconVariants> &
  React.HTMLAttributes<HTMLDivElement>

export const RiskBadge = ({
  riskLevel,
  size: sizeProp,
  variant = 'default',
  icon,
  className,
}: RiskBadgeProps) => {
  const meta = RISK_LEVEL_MAP[riskLevel]
  const label = riskLevelLabel[riskLevel]
  if (!meta || !label) return null

  const size = sizeProp ?? (variant === 'default' ? 'default' : 'large')
  const isIconOnly = size === 'icon'
  const isEstimated = variant === 'estimated'
  const Icon = icon ?? meta.icon

  const iconNode = <Icon className={iconVariants({ size })} aria-hidden='true' />

  return (
    <Badge
      variant={meta.variant}
      size={size}
      badgeStyle='subtle'
      className={cn(
        isEstimated && estimatedBorderVariants({ riskLevel: riskLevel as SupportedRiskLevel }),
        className,
      )}
      aria-label={isIconOnly ? label : undefined}
    >
      {iconNode}
      {!isIconOnly && <span>{label}</span>}
    </Badge>
  )
}
